import React, { useEffect, useRef, useState } from "react";

import Button from "core-web/components/base/Button";
import CrossIcon from "theme/components/icons/CrossIcon";
import MinusIcon from "theme/components/icons/MinusIcon";
import PlusIcon from "theme/components/icons/PlusIcon";
import PropTypes from "prop-types";
import TextStyles from "theme/components/text/TextStyles";
import styled from "theme/libs/styled";
import { useSelector } from "react-redux";
import { useTheme } from "emotion-theming";
import { useTranslation } from "react-i18next";

const Wrapper = styled("div")`
    position: relative;
    display: flex;
    align-items: center;
    border-radius: 40px;
    color: ${({ theme }) => theme.colors.black_70};
    border: 1px solid ${({ theme }) => theme.colors.black_70};

    &[data-is-changeable="false"] {
        opacity: 0.4;
    }
`;

const AmountButton = styled(Button)`
    display: flex;
    align-items: center;
    height: 100%;
`;

// const Warning = styled("p")`
//     position: absolute;
//     width: max-content;
//     left: 0;
//     top: -20px;
//     color: ${({ theme }) => theme.colors.red};
// `;

const AmountInput = ({
    isBasket = false,
    isPdp = false,
    isChangeable = true,
    defaultValue = 1,
    maxLength = 999,
    minLength = 1,
    onChange = () => null,
    productId = null,
    isDisabledDown = false,
    ...rest
}) => {
    const { t } = useTranslation();
    const timerRef = useRef(null);
    const isInitialMount = useRef(true);
    const { colors } = useTheme();
    const basketItems = useSelector(({ basket }) => basket.items);
    const productInBasket = basketItems.find((item) => item.product_id === productId);
    const productInBasketPackage = basketItems.reduce((quantity, item) => {
        const matchedItem = (item.included_products || []).find((includedProduct) => includedProduct.id === productId);
        if (matchedItem && item.quantity && matchedItem.quantity) {
            quantity += item.quantity * matchedItem.quantity;
        }

        return quantity;
    }, 0);
    const productQuantityInBasket = (productInBasket?.quantity || 0) + productInBasketPackage;
    const availableQuantity = maxLength - productQuantityInBasket > 0 ? maxLength - productQuantityInBasket : 0;

    const [quantity, setQuantity] = useState(defaultValue);
    const [displayWarning, setDisplayWarning] = useState(false);

    useEffect(() => {
        return () => clearTimeout(timerRef.current);
    }, []);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else {
            if (defaultValue > availableQuantity) {
                setDisplayWarning(true);
                timerRef.current = setTimeout(() => setDisplayWarning(false), 4000);
            }
        }

        if (defaultValue !== quantity) {
            setQuantity(defaultValue);
        }
    }, [defaultValue]);

    const checkValue = (amount, event) => {
        if (timerRef.current) {
            setDisplayWarning(false);
            clearTimeout(timerRef.current);
        }

        const value = parseInt(amount, 10);

        if (isNaN(value) || value < minLength) {
            setQuantity(defaultValue);
        } else if (value > availableQuantity) {
            onChange(availableQuantity || 1, event);
            setQuantity(availableQuantity || 1);
            setDisplayWarning(true);
            timerRef.current = setTimeout(() => setDisplayWarning(false), 4000);
        } else if (value !== defaultValue) {
            onChange(value, event);
        }
    };

    useEffect(() => {
        if (!isBasket && quantity > availableQuantity) {
            onChange(availableQuantity || 1);
        }

        if (quantity <= availableQuantity) {
            setDisplayWarning(false);
        }
    }, [availableQuantity]);

    const onInput = (event) => {
        const value = event.target.value;
        if (!isNaN(value)) {
            setQuantity(value);
        }
        if (timerRef.current) {
            setDisplayWarning(false);
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => checkValue(value, event), 2000);
    };

    const onButtonClick = (event, clickType) => {
        const isDisabledAddButton = event.currentTarget.classList.contains("disabled") && clickType === "add";

        if (isDisabledAddButton) {
            setDisplayWarning(true);
            timerRef.current = setTimeout(() => setDisplayWarning(false), 4000);
            return;
        }

        const value = parseInt(event.currentTarget.value, 10);
        checkValue(value);
    };

    const DecreaseIcon = minLength === 0 && quantity === 1 ? CrossIcon : MinusIcon;

    return (
        <Wrapper data-is-changeable={isChangeable} {...rest}>
            <AmountButton
                ariaLabel={t("product_card.quantity_decrease")}
                value={quantity - 1}
                disabled={!isChangeable || quantity === minLength || quantity === 0 || isDisabledDown}
                onClick={quantity !== 0 && onButtonClick}
                p={isBasket ? "0 8px 0 16px" : "0 8px 0 20px"}
            >
                <DecreaseIcon color={colors.black_70} width="12px" height="12px" />
            </AmountButton>
            <TextStyles
                as="input"
                textStyles={isPdp ? "Myriad Pro/18-100-semibold-semiextended" : "Myriad Pro/14-100-semibold"}
                value={quantity}
                disabled={!isChangeable}
                quantity={quantity}
                onBlur={(e) => checkValue(e.target.value)}
                pattern="[0-9]*"
                type="numeric"
                color={colors.black_70}
                minWidth="2ch"
                maxWidth="4ch"
                border="none"
                outline="none"
                padding="0"
                background="none"
                textAlign="center"
                onChange={onInput}
            />
            <AmountButton
                ariaLabel={t("product_card.quantity_decrease")}
                value={quantity + 1}
                disabled={!isChangeable}
                onClick={(e) => onButtonClick(e, "add")}
                p={isBasket ? "0 16px 0 8px" : "0 20px 0 8px"}
            >
                <PlusIcon color={colors.black_70} width="12px" height="12px" />
            </AmountButton>
        </Wrapper>
    );
};

AmountInput.propTypes = {
    defaultValue: PropTypes.number,
    isBasket: PropTypes.bool,
    isChangeable: PropTypes.bool,
    maxLength: PropTypes.number,
    minLength: PropTypes.number,
    onChange: PropTypes.func,
    productId: PropTypes.number,
    maxAmountWarning: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    // isDisabledUp: PropTypes.bool,
    isDisabledDown: PropTypes.bool,
};

export default AmountInput;
